import { useEffect } from 'react';

/**
 * Keyboard shortcuts to intercept for screen capture protection.
 * Validates: Req 1.29
 */
const BLOCKED_SHORTCUTS = [
  { key: 'PrintScreen' },
  { key: 'p', ctrlKey: true },
  { key: '3', metaKey: true, shiftKey: true },
  { key: '4', metaKey: true, shiftKey: true },
  { key: '5', metaKey: true, shiftKey: true },
];

/**
 * Checks if a keyboard event matches any blocked shortcut.
 */
function isBlockedShortcut(event: KeyboardEvent): boolean {
  return BLOCKED_SHORTCUTS.some((shortcut) => {
    if (event.key !== shortcut.key) return false;
    if ('ctrlKey' in shortcut && shortcut.ctrlKey && !event.ctrlKey) return false;
    if ('metaKey' in shortcut && shortcut.metaKey && !event.metaKey) return false;
    if ('shiftKey' in shortcut && shortcut.shiftKey && !event.shiftKey) return false;
    return true;
  });
}

/**
 * useScreenCaptureGuard — Intercepts PrintScreen, Ctrl+P, and
 * Cmd+Shift+3/4/5 keyboard shortcuts to prevent screen capture.
 *
 * Calls preventDefault() and stopPropagation() on matched events.
 *
 * Validates: Req 1.29
 */
export function useScreenCaptureGuard(): void {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (isBlockedShortcut(event)) {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    // Use capture phase to intercept before other handlers
    document.addEventListener('keydown', handleKeyDown, true);
    document.addEventListener('keyup', handleKeyDown, true);

    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
      document.removeEventListener('keyup', handleKeyDown, true);
    };
  }, []);
}
